import React from 'react';
import styled from '@emotion/styled';
import { Text } from 'rebass';
import { MdCheck } from 'react-icons/md';
import { BiHelpCircle } from 'react-icons/bi';
import { colors, fonts, DESKTOP } from '../tokens';
import Chevron from './Chevron';
// Static marketing content, matching bundle-comparison.png exactly — this is a
// presentational recreation of that design, not a live read of purchase
// options (prices/discounts here are list copy, not per-journal data from
// GET .../purchase_options).
const PLANS = [
  { name: 'Starter', save: '32%', listPrice: '$73.78', dollars: '49', cents: '99' },
  { name: 'Premium', save: '26%', listPrice: '$100.67', dollars: '69', cents: '99' },
  { name: 'Signature', save: '44%', listPrice: '$177.56', dollars: '99', cents: '99', popular: true },
  { name: 'Platinum', save: '50%', listPrice: '$261.33', dollars: '129', cents: '99' },
];

// A row's values: `true` renders the filled checkmark, `null`/`undefined`
// renders the dash, anything else renders as-is.
const SECTIONS = [
  {
    title: 'Book',
    rows: [
      { label: 'Size (Inches)', values: ['8½ x 11', '8½ x 11', '8½ x 11', '8½ x 11'] },
      { label: 'Cover', values: ['Softcover', 'Softcover', 'Hardcover', 'Hardcover'] },
      { label: '# of Pages Included', hint: true, values: ['40', '50', '60', '80'] },
      { label: 'Subscription (Months)', values: ['12', '12', '12', '12'] },
    ],
  },
  {
    title: 'Discounts',
    rows: [
      { label: 'Discount for extra pages (if needed)', values: [null, '10% off', '20% off', '25% off'] },
      { label: 'Discount for extra book copies', values: ['5% off', '10% off', '15% off', '20% off'] },
    ],
  },
  {
    title: 'Specialty Pages',
    rows: [
      { label: 'Milestones', hint: true, values: [null, true, true, true] },
      { label: 'Watch Me Grow', hint: true, values: [null, null, true, true] },
      { label: 'Growth Chart', hint: true, values: [null, null, null, true] },
      { label: 'Talking Family Tree', hint: true, values: [null, null, null, true] },
    ],
  },
  {
    title: 'Extras',
    rows: [{ label: 'E-book (coming soon)', values: [null, null, '50% off', true] }],
  },
];

// Exact pixel values sampled from bundle-comparison.png — kept local to this
// component rather than promoted to the shared token file, since nothing else
// in the app uses this palette.
const GREEN_PILL_BG = '#CEFDD7';
const LAVENDER_BG = '#E9E2F8';
const POPULAR_BORDER = '#DDBAF0';
const LABEL_TEXT = '#39495C';
const ROW_BORDER = '#E6E6EE';

const Wrap = styled.div`
  background: ${colors.pageBg};
  margin-bottom: 90px;
`;

const Title = styled.h2`
  font-family: ${fonts.heading};
  font-weight: 700;
  font-size: 26px;
  text-align: center;
  color: ${colors.heading};
  margin: 0 0 10px;
`;

const Subtitle = styled.p`
  font-family: ${fonts.body};
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: ${colors.muted};
  margin: 0 0 24px;
`;

// The table needs more width than a 4-column grid comfortably gives on a
// phone screen — not something the static design has to solve, but this does
// have to actually work at mobile widths, so it scrolls horizontally there.
const ScrollArea = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 8px;
`;

const Table = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(170px, 1.7fr) repeat(4, minmax(92px, 1fr));
  align-items: stretch;
  min-width: 680px;
      padding-top: 20px;
`;

const HeaderCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 6px 6px 14px;
`;

const SavePill = styled.span`
  display: inline-block;
  background: ${GREEN_PILL_BG};
  color: #0e0e0e;
  font-family: ${fonts.body};
  font-weight: 700;
  font-size: 14px;
  padding: 5px 16px;
  border-radius: 9999px;
  white-space: nowrap;
`;

const PlanName = styled.p`
  font-family: ${fonts.heading};
  font-weight: 400;
  font-size: 23px;
  color: ${colors.heading};
  margin: 8px 0 0;
`;

const ListPrice = styled.p`
  font-family: ${fonts.body};
  font-size: 14px;
  color: ${colors.muted};
  text-decoration: line-through;
  margin: 2px 0 0;
`;

const Price = styled.p`
  font-family: ${fonts.body};
  font-weight: 700;
  font-size: 19px;
  color: ${colors.text};
  margin: 0;

  sup {
    font-size: 12px;
    font-weight: 700;
    top: -0.6em;
  }
`;

const SectionHeader = styled.div`
  grid-column: 1 / -1;
  background: ${LAVENDER_BG};
  border: 1px solid ${POPULAR_BORDER};
  border-radius: 10px;
  padding: 12px;
  margin: 10px 0 4px;
  text-align: center;
  font-family: ${fonts.body};
  font-weight: 700;
  font-size: 17px;
  color: #2e2a42;
  padding-left: 45px;
  text-align: justify;
  ${DESKTOP} {
    padding-left: 140px;
  }
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  padding: 13px 10px;
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 13px;
  line-height: 1.3;
  color: ${LABEL_TEXT};
  border-bottom: 1px solid ${ROW_BORDER};
  text-align: right;
`;

const HintIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${colors.brand};
  opacity: 0.55;
`;

const Value = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 13px 6px;
  font-family: ${fonts.body};
  font-size: 14px;
  color: ${colors.text};
  border-bottom: 1px solid ${ROW_BORDER};
`;

const Dash = styled.span`
  color: ${colors.muted};
`;

const CheckCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${colors.brand};
  color: #fff;
`;

// The "Most Popular" plan gets its own rounded border running the full height
// of the table — a grid item placed to overlap every row in that column
// rather than something stitched together per-row, so it stays correct no
// matter how many rows the sections above have.
const PopularBox = styled.div`
  position: absolute;
  grid-row: 1 / ${props => props.totalRows + 1};
  grid-column: ${props => props.column} / ${props => props.column + 1};
  border: 1.5px solid ${POPULAR_BORDER};
  border-radius: 12px;
  pointer-events: none;
  height: calc(100% + 16px);
  padding-top: 11px;
  margin-top: -16px;
  margin-left: 0px;
  ${DESKTOP} {
    margin-left: 20px;
  }
`;

const PopularBadge = styled.div`
  position: relative;
  border-radius: 12px;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  background: ${colors.pageBg};
  padding: 0 6px;
  font-family: ${fonts.body};
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${colors.brand};
`;

// Static recreation of bundle-comparison.png — the member taps a destination
// plan in Upgrade Bundle mode and sees exactly this, in-app.
const BundleComparison = () => {
  const totalRows = 4 + SECTIONS.reduce((sum, section) => sum + 1 + section.rows.length, 0);
  const popularPlanIndex = PLANS.findIndex(plan => plan.popular);
  const popularColumn = popularPlanIndex + 2; // 1-based; column 1 is the label.

  return (
    <Wrap>
      <Title>Compare bundles at a glance</Title>
      <Subtitle>
        Each bundle includes a 12 month app subscription, extra pages, special features and future discounts.
      </Subtitle>

      <ScrollArea>
        <Table>
          {/* {popularPlanIndex > -1 && ( */}
          <PopularBox totalRows={totalRows} column={popularColumn}>
            <PopularBadge>
              Most Popular <span role="img" aria-label="">👍</span>
            </PopularBadge>
          </PopularBox>
          {/* )} */}

          <HeaderCell />
          {PLANS.map(plan => (
            <HeaderCell key={`save-${plan.name}`}>
              <SavePill>Save {plan.save}</SavePill>
            </HeaderCell>
          ))}

          <HeaderCell />
          {PLANS.map(plan => (
            <HeaderCell key={`name-${plan.name}`}>
              <PlanName>{plan.name}</PlanName>
            </HeaderCell>
          ))}

          <HeaderCell />
          {PLANS.map(plan => (
            <HeaderCell key={`list-${plan.name}`}>
              <ListPrice>{plan.listPrice}</ListPrice>
            </HeaderCell>
          ))}

          <HeaderCell />
          {PLANS.map(plan => (
            <HeaderCell key={`price-${plan.name}`}>
              <Price>
                ${plan.dollars}
                <sup>.{plan.cents}</sup>
              </Price>
            </HeaderCell>
          ))}

          <Chevron />
          {SECTIONS.map((section, index) => (
            <React.Fragment key={section.title}>
              <SectionHeader>
                <Text width="150px" textAlign="right" fontFamily="Poppins" fontSize={17} fontWeight={700} lineHeight="22px" letterSpacing="-0.096px" color="#2E2A42">
                  {section.title}
                </Text>
              </SectionHeader>
              {section.rows.map(row => (
                <React.Fragment key={row.label}>
                  <RowLabel>
                    {row.label}
                    {row.hint && (
                      <HintIcon>
                        <BiHelpCircle size={15} />
                      </HintIcon>
                    )}
                  </RowLabel>
                  {row.values.map((value, columnIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Value key={columnIndex}>
                      {value === true ? (
                        <CheckCircle>
                          <MdCheck size={16} />
                        </CheckCircle>
                      ) : value ? (
                        value
                      ) : (
                        <Dash>–</Dash>
                      )}
                    </Value>
                  ))}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </Table>
      </ScrollArea>
    </Wrap>
  );
};

export default BundleComparison;
